import React, { useEffect, useMemo, useState } from 'react';
import TableWithFilters from './shared/TableWithFilters';
import PageTitle from './shared/PageTitle';
import { headers } from '../content';
import { makeStyles } from 'tss-react/mui';
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
} from '@apollo/client';
import { getLovs } from '../queries';
import { IFormValues } from './ViewBreakdown';
import { enumListAsRecordObject, getError } from '@utils/graph-utils';
import { transformPolicyArrayToObject } from '../utils';
import {
  EnhancedUpdatedTableFilterType,
  IEnhancedMenuItem,
  IFilterModel,
} from '@components/table/types';
import ViewBreakdownShell from './ViewBreakdownShell';
import { IAbstractRecord } from '@models';
import DateService from '@services/DateService';
import { IListingData } from '@models/listing';
import { cloneDeep } from 'lodash';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getFilter } from '@utils/filter-utils';

interface IMainColumnProps {
  mainEntityId: string;
  fetchPolicies: LazyQueryExecFunction<any, OperationVariables>;
  data: IAbstractRecord;
  loading: boolean;
}

const useStyles = makeStyles()(() => ({
  container: {
    marginTop: '36px',
  },
}));

const FILTER_SESSION_KEY = 'RPsPolicesFilter';

const BrokerMainColumn: React.FC<IMainColumnProps> = ({
  mainEntityId,
  fetchPolicies,
  data,
  loading,
}) => {
  const { classes } = useStyles();
  const [fetchLovs, { data: lovs }] = useLazyQuery(getLovs);
  const [viewBreakdownPopup, setViewBreakdownPopup] = useState<{
    open: boolean;
    selected?: {
      id: string;
    } & IFormValues;
  }>({
    open: false,
  });

  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          PolicyIssuanceDate: [],
          Line: [],
          Currency: [],
        };

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialFilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
  });

  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageNumber: 1,
    pageSize: 10,
    totalCount: 0,
  });

  const getListingData = async (filter = filterValues): Promise<void> => {
    if (!filter || !lovs) {
      return;
    }

    try {
      const formattedDateRange = DateService.formatDateRangeBackend(
        filter.namedFilters.PolicyIssuanceDate
      );

      const { data, error } = await fetchPolicies({
        variables: {
          pagination: filter.pagination,
          OrderByField: filter.orderBy,
          DescendingField: filter.descending,
          SelectedLine: filter.namedFilters.Line || undefined,
          SelectedCurrency: filter.namedFilters.Currency || undefined,
          PolicyIssuanceFrom: formattedDateRange?.[0],
          PolicyIssuanceTo: formattedDateRange?.[1],
          BusinessPartnerID: mainEntityId,
        },
        errorPolicy: 'all',
      });

      if (error) {
        toast.error(
          <ToastErrorMessage>{getError({ errors: error })}</ToastErrorMessage>
        );
      }

      if (data) {
        const paymentDivisions = enumListAsRecordObject(
          lovs.Production_PaymentDivisionTypeList?.enumValues as {
            __typename: string;
            name: string;
          }[]
        );

        const queryData =
          data?.Accounting?.queries?.BrokerPoliciesListPaginated;

        const items = transformPolicyArrayToObject(
          queryData.items,
          paymentDivisions
        );

        setTableData({
          pagedItems: items,
          pageNumber: queryData.paging.pageNumber,
          pageSize: queryData.paging.pageSize,
          totalCount: queryData.paging.totalCount,
        });
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  function handlePageChange(filter = filterValues): Promise<void> {
    return new Promise<void>(() => getListingData(filter));
  }

  useEffect(() => {
    fetchLovs();
  }, [mainEntityId]);

  useEffect(() => {
    if (lovs) {
      getListingData();
    }
  }, [lovs]);

  const tableFilters: Record<string, EnhancedUpdatedTableFilterType> =
    useMemo(() => {
      if (!lovs) return {};

      const allLinesArray: Record<string, string>[] =
        lovs?.PlanConfigManagement?.queries?.allLines;
      const allLines: Record<string, string> = {};
      allLinesArray?.forEach((item) => {
        allLines[item.planConfigManagement_Line_Id] =
          item.planConfigManagement_Line_Name;
      });

      return {
        Line: {
          name: 'Line',
          type: 'select',
          options: allLines,
          placeholder: 'Line',
          selectedValues: [],
        },
        PolicyIssuanceDate: {
          name: 'PolicyIssuanceDate',
          type: 'daterange',
          placeholder: 'Policy Issuance Date',
          value: [],
        },
        Currency: {
          name: 'Currency',
          type: 'select',
          placeholder: 'Currency',
          selectedValues: [],
          injectGlobalLov: {
            type: 'currency',
            value: 'Code',
            display: 'Symbol',
          },
        },
      };
    }, [lovs]);

  const actions: IEnhancedMenuItem[] = [
    {
      name: 'ViewBreakdown',
      title: 'View Breakdown',
      onClick: (selected: string[]) => {
        setViewBreakdownPopup({
          open: true,
          selected: {
            id: tableData.pagedItems?.[selected?.[0]].policyId,
            ...tableData.pagedItems?.[selected?.[0]],
          },
        });
      },
    },
  ];

  return (
    <div>
      {viewBreakdownPopup.open && (
        <ViewBreakdownShell
          open={viewBreakdownPopup.open}
          policyId={viewBreakdownPopup.selected?.id}
          businessPartnerId={mainEntityId}
          data={viewBreakdownPopup.selected}
          onClose={() => {
            setViewBreakdownPopup({
              open: false,
              selected: undefined,
            });
          }}
        />
      )}
      <PageTitle text={'Receivables & Payables'} />
      <div className={classes.container}>
        <TableWithFilters
          inlineTitle="Policies"
          loading={loading}
          actions={actions}
          headers={headers}
          data={tableData}
          tableFilters={tableFilters}
          applyFilters={(values) => {
            const newFilter = cloneDeep(filterValues);
            newFilter.namedFilters = values;
            setFilterValues(newFilter);
            getListingData(newFilter);
          }}
          onPageChange={(filterModel) => {
            const newFilterModel = {
              ...filterValues,
              ...filterModel,
            };
            setFilterValues(newFilterModel);
            return handlePageChange(newFilterModel);
          }}
        />
      </div>
    </div>
  );
};

export default BrokerMainColumn;
